import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  MenuItem,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import axios from 'axios';
import { useThemeMode } from '../contexts/ThemeModeContext';

interface DemoRequestFormProps {
  open: boolean;
  onClose: () => void;
  isPricingRequest?: boolean;
}

const DemoRequestForm: React.FC<DemoRequestFormProps> = ({ open, onClose, isPricingRequest = false }) => {
  const { mode } = useThemeMode();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    title: '',
    company_size: '',
    use_case: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const { getConfig } = require('../config/environment');
  const config = getConfig();
  const API_URL = config.apiUrl;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/v1/demo-request`, formData);
      
      if (response.data.success) {
        setSuccess(true);
        // Reset form
        setFormData({
          name: '',
          email: '',
          company: '',
          title: '',
          company_size: '',
          use_case: '',
          message: '',
        });
        // Close after 2 seconds
        setTimeout(() => {
          setSuccess(false);
          onClose();
        }, 2000);
      }
    } catch (err: any) {
      // Provide more detailed error messages
      console.error('Demo request error:', {
        status: err.response?.status,
        data: err.response?.data,
        message: err.message,
        stack: err.stack
      });
      
      const errorMessage = err.response?.data?.detail 
        || err.message 
        || (err.response?.status === 400 ? 'Invalid request. Please check your input.' : 'Failed to submit demo request. Please try again.');
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          background: mode === 'dark'
            ? 'linear-gradient(135deg, rgba(15, 23, 42, 0.98), rgba(30, 41, 59, 0.98))'
            : 'rgba(255, 255, 255, 0.98)',
          backdropFilter: 'blur(20px)',
        },
      }}
    >
      <DialogTitle sx={{ 
        pb: 2,
        borderBottom: mode === 'dark' ? '1px solid rgba(71, 85, 105, 0.4)' : '1px solid rgba(226, 232, 240, 0.8)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: mode === 'dark' ? '#fff' : '#0f172a' }}>
          {isPricingRequest ? 'Request Pricing' : 'Request a Demo'}
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: mode === 'dark' ? 'rgba(255,255,255,0.7)' : '#64748b' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        {success ? (
          <Alert 
            severity="success" 
            sx={{ 
              mb: 2,
              backgroundColor: mode === 'dark' ? 'rgba(16, 185, 129, 0.15)' : 'rgba(16, 185, 129, 0.1)',
              color: mode === 'dark' ? '#34d399' : '#059669',
              border: mode === 'dark' ? '1px solid rgba(16, 185, 129, 0.3)' : '1px solid rgba(16, 185, 129, 0.2)',
              '& .MuiAlert-icon': {
                color: mode === 'dark' ? '#34d399' : '#10b981',
              },
            }}
          >
            {isPricingRequest 
              ? "Pricing request submitted successfully! We'll contact you soon with custom pricing information."
              : "Demo request submitted successfully! We'll contact you soon."}
          </Alert>
        ) : (
          <>
            <Typography variant="body2" sx={{ color: mode === 'dark' ? 'rgba(255,255,255,0.7)' : '#64748b', mb: 3 }}>
              {isPricingRequest 
                ? "Tell us about your organization and we'll provide custom enterprise pricing tailored to your needs."
                : "Tell us about your organization and we'll schedule a personalized demo to show you how CoreSupport can transform your support operations."}
            </Typography>
            <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <TextField
                required
                name="name"
                label="Full Name"
                value={formData.name}
                onChange={handleChange}
                fullWidth
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: mode === 'dark' ? '#1e293b !important' : 'rgba(248, 250, 252, 0.8)',
                    '& .MuiInputBase-input': {
                      color: mode === 'dark' ? '#ffffff !important' : '#0f172a !important',
                      WebkitTextFillColor: mode === 'dark' ? '#ffffff !important' : '#0f172a !important',
                    },
                    '& input': {
                      color: mode === 'dark' ? '#ffffff !important' : '#0f172a !important',
                      WebkitTextFillColor: mode === 'dark' ? '#ffffff !important' : '#0f172a !important',
                    },
                    '& input::placeholder': {
                      color: mode === 'dark' ? 'rgba(255, 255, 255, 0.5) !important' : '#94a3b8 !important',
                      opacity: 1,
                      WebkitTextFillColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.5) !important' : '#94a3b8 !important',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: mode === 'dark' ? 'rgba(71, 85, 105, 0.4)' : 'rgba(226, 232, 240, 0.8)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: mode === 'dark' ? 'rgba(99, 102, 241, 0.6)' : 'rgba(99, 102, 241, 0.4)',
                    },
                    '&.Mui-focused': {
                      backgroundColor: mode === 'dark' ? '#1e293b !important' : 'rgba(248, 250, 252, 0.8)',
                      '& .MuiInputBase-input': {
                        color: mode === 'dark' ? '#ffffff !important' : '#0f172a !important',
                        WebkitTextFillColor: mode === 'dark' ? '#ffffff !important' : '#0f172a !important',
                      },
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#6366f1',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : '#64748b',
                    '&.Mui-focused': {
                      color: '#6366f1',
                    },
                  },
                }}
              />
              <TextField
                required
                name="email"
                label="Email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                fullWidth
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: mode === 'dark' ? '#1e293b !important' : 'rgba(248, 250, 252, 0.8)',
                    '& .MuiInputBase-input': {
                      color: mode === 'dark' ? '#ffffff !important' : '#0f172a !important',
                      WebkitTextFillColor: mode === 'dark' ? '#ffffff !important' : '#0f172a !important',
                    },
                    '& input': {
                      color: mode === 'dark' ? '#ffffff !important' : '#0f172a !important',
                      WebkitTextFillColor: mode === 'dark' ? '#ffffff !important' : '#0f172a !important',
                    },
                    '& input::placeholder': {
                      color: mode === 'dark' ? 'rgba(255, 255, 255, 0.5) !important' : '#94a3b8 !important',
                      opacity: 1,
                      WebkitTextFillColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.5) !important' : '#94a3b8 !important',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: mode === 'dark' ? 'rgba(71, 85, 105, 0.4)' : 'rgba(226, 232, 240, 0.8)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: mode === 'dark' ? 'rgba(99, 102, 241, 0.6)' : 'rgba(99, 102, 241, 0.4)',
                    },
                    '&.Mui-focused': {
                      backgroundColor: mode === 'dark' ? '#1e293b !important' : 'rgba(248, 250, 252, 0.8)',
                      '& .MuiInputBase-input': {
                        color: mode === 'dark' ? '#ffffff !important' : '#0f172a !important',
                        WebkitTextFillColor: mode === 'dark' ? '#ffffff !important' : '#0f172a !important',
                      },
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#6366f1',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : '#64748b',
                    '&.Mui-focused': {
                      color: '#6366f1',
                    },
                  },
                }}
              />
              <TextField
                name="company"
                label="Company"
                value={formData.company}
                onChange={handleChange}
                fullWidth
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: mode === 'dark' ? '#1e293b !important' : 'rgba(248, 250, 252, 0.8)',
                    '& .MuiInputBase-input': {
                      color: mode === 'dark' ? '#ffffff !important' : '#0f172a !important',
                      WebkitTextFillColor: mode === 'dark' ? '#ffffff !important' : '#0f172a !important',
                    },
                    '& input': {
                      color: mode === 'dark' ? '#ffffff !important' : '#0f172a !important',
                      WebkitTextFillColor: mode === 'dark' ? '#ffffff !important' : '#0f172a !important',
                    },
                    '& input::placeholder': {
                      color: mode === 'dark' ? 'rgba(255, 255, 255, 0.5) !important' : '#94a3b8 !important',
                      opacity: 1,
                      WebkitTextFillColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.5) !important' : '#94a3b8 !important',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: mode === 'dark' ? 'rgba(71, 85, 105, 0.4)' : 'rgba(226, 232, 240, 0.8)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: mode === 'dark' ? 'rgba(99, 102, 241, 0.6)' : 'rgba(99, 102, 241, 0.4)',
                    },
                    '&.Mui-focused': {
                      backgroundColor: mode === 'dark' ? '#1e293b !important' : 'rgba(248, 250, 252, 0.8)',
                      '& .MuiInputBase-input': {
                        color: mode === 'dark' ? '#ffffff !important' : '#0f172a !important',
                        WebkitTextFillColor: mode === 'dark' ? '#ffffff !important' : '#0f172a !important',
                      },
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#6366f1',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : '#64748b',
                    '&.Mui-focused': {
                      color: '#6366f1',
                    },
                  },
                }}
              />
              <TextField
                name="title"
                label="Job Title"
                value={formData.title}
                onChange={handleChange}
                fullWidth
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: mode === 'dark' ? '#1e293b !important' : 'rgba(248, 250, 252, 0.8)',
                    '& .MuiInputBase-input': {
                      color: mode === 'dark' ? '#ffffff !important' : '#0f172a !important',
                      WebkitTextFillColor: mode === 'dark' ? '#ffffff !important' : '#0f172a !important',
                    },
                    '& input': {
                      color: mode === 'dark' ? '#ffffff !important' : '#0f172a !important',
                      WebkitTextFillColor: mode === 'dark' ? '#ffffff !important' : '#0f172a !important',
                    },
                    '& input::placeholder': {
                      color: mode === 'dark' ? 'rgba(255, 255, 255, 0.5) !important' : '#94a3b8 !important',
                      opacity: 1,
                      WebkitTextFillColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.5) !important' : '#94a3b8 !important',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: mode === 'dark' ? 'rgba(71, 85, 105, 0.4)' : 'rgba(226, 232, 240, 0.8)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: mode === 'dark' ? 'rgba(99, 102, 241, 0.6)' : 'rgba(99, 102, 241, 0.4)',
                    },
                    '&.Mui-focused': {
                      backgroundColor: mode === 'dark' ? '#1e293b !important' : 'rgba(248, 250, 252, 0.8)',
                      '& .MuiInputBase-input': {
                        color: mode === 'dark' ? '#ffffff !important' : '#0f172a !important',
                        WebkitTextFillColor: mode === 'dark' ? '#ffffff !important' : '#0f172a !important',
                      },
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#6366f1',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : '#64748b',
                    '&.Mui-focused': {
                      color: '#6366f1',
                    },
                  },
                }}
              />
              <TextField
                name="company_size"
                label="Company Size"
                select
                value={formData.company_size}
                onChange={handleChange}
                fullWidth
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: mode === 'dark' ? '#1e293b !important' : 'rgba(248, 250, 252, 0.8)',
                    '& .MuiInputBase-input': {
                      color: mode === 'dark' ? '#ffffff !important' : '#0f172a !important',
                      WebkitTextFillColor: mode === 'dark' ? '#ffffff !important' : '#0f172a !important',
                    },
                    '& input': {
                      color: mode === 'dark' ? '#ffffff !important' : '#0f172a !important',
                      WebkitTextFillColor: mode === 'dark' ? '#ffffff !important' : '#0f172a !important',
                    },
                    '& input::placeholder': {
                      color: mode === 'dark' ? 'rgba(255, 255, 255, 0.5) !important' : '#94a3b8 !important',
                      opacity: 1,
                      WebkitTextFillColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.5) !important' : '#94a3b8 !important',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: mode === 'dark' ? 'rgba(71, 85, 105, 0.4)' : 'rgba(226, 232, 240, 0.8)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: mode === 'dark' ? 'rgba(99, 102, 241, 0.6)' : 'rgba(99, 102, 241, 0.4)',
                    },
                    '&.Mui-focused': {
                      backgroundColor: mode === 'dark' ? '#1e293b !important' : 'rgba(248, 250, 252, 0.8)',
                      '& .MuiInputBase-input': {
                        color: mode === 'dark' ? '#ffffff !important' : '#0f172a !important',
                        WebkitTextFillColor: mode === 'dark' ? '#ffffff !important' : '#0f172a !important',
                      },
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#6366f1',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : '#64748b',
                    '&.Mui-focused': {
                      color: '#6366f1',
                    },
                  },
                  '& .MuiSelect-select': {
                    color: mode === 'dark' ? '#ffffff' : '#0f172a',
                  },
                }}
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      sx: {
                        backgroundColor: mode === 'dark' ? 'rgba(15, 23, 42, 0.98)' : 'rgba(255, 255, 255, 0.98)',
                        backdropFilter: 'blur(20px)',
                        '& .MuiMenuItem-root': {
                          color: mode === 'dark' ? '#ffffff' : '#0f172a',
                          '&:hover': {
                            backgroundColor: mode === 'dark' ? 'rgba(99, 102, 241, 0.15)' : 'rgba(99, 102, 241, 0.08)',
                          },
                          '&.Mui-selected': {
                            backgroundColor: mode === 'dark' ? 'rgba(99, 102, 241, 0.25)' : 'rgba(99, 102, 241, 0.15)',
                          },
                        },
                      },
                    },
                  },
                }}
              >
                <MenuItem value="">Select size</MenuItem>
                <MenuItem value="1-10">1-10 employees</MenuItem>
                <MenuItem value="11-50">11-50 employees</MenuItem>
                <MenuItem value="51-200">51-200 employees</MenuItem>
                <MenuItem value="201-500">201-500 employees</MenuItem>
                <MenuItem value="501-1000">501-1000 employees</MenuItem>
                <MenuItem value="1000+">1000+ employees</MenuItem>
              </TextField>
              <TextField
                name="use_case"
                label="Primary Use Case"
                select
                value={formData.use_case}
                onChange={handleChange}
                fullWidth
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: mode === 'dark' ? '#1e293b !important' : 'rgba(248, 250, 252, 0.8)',
                    '& .MuiInputBase-input': {
                      color: mode === 'dark' ? '#ffffff !important' : '#0f172a !important',
                      WebkitTextFillColor: mode === 'dark' ? '#ffffff !important' : '#0f172a !important',
                    },
                    '& input': {
                      color: mode === 'dark' ? '#ffffff !important' : '#0f172a !important',
                      WebkitTextFillColor: mode === 'dark' ? '#ffffff !important' : '#0f172a !important',
                    },
                    '& input::placeholder': {
                      color: mode === 'dark' ? 'rgba(255, 255, 255, 0.5) !important' : '#94a3b8 !important',
                      opacity: 1,
                      WebkitTextFillColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.5) !important' : '#94a3b8 !important',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: mode === 'dark' ? 'rgba(71, 85, 105, 0.4)' : 'rgba(226, 232, 240, 0.8)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: mode === 'dark' ? 'rgba(99, 102, 241, 0.6)' : 'rgba(99, 102, 241, 0.4)',
                    },
                    '&.Mui-focused': {
                      backgroundColor: mode === 'dark' ? '#1e293b !important' : 'rgba(248, 250, 252, 0.8)',
                      '& .MuiInputBase-input': {
                        color: mode === 'dark' ? '#ffffff !important' : '#0f172a !important',
                        WebkitTextFillColor: mode === 'dark' ? '#ffffff !important' : '#0f172a !important',
                      },
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#6366f1',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : '#64748b',
                    '&.Mui-focused': {
                      color: '#6366f1',
                    },
                  },
                  '& .MuiSelect-select': {
                    color: mode === 'dark' ? '#ffffff' : '#0f172a',
                  },
                }}
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      sx: {
                        backgroundColor: mode === 'dark' ? 'rgba(15, 23, 42, 0.98)' : 'rgba(255, 255, 255, 0.98)',
                        backdropFilter: 'blur(20px)',
                        '& .MuiMenuItem-root': {
                          color: mode === 'dark' ? '#ffffff' : '#0f172a',
                          '&:hover': {
                            backgroundColor: mode === 'dark' ? 'rgba(99, 102, 241, 0.15)' : 'rgba(99, 102, 241, 0.08)',
                          },
                          '&.Mui-selected': {
                            backgroundColor: mode === 'dark' ? 'rgba(99, 102, 241, 0.25)' : 'rgba(99, 102, 241, 0.15)',
                          },
                        },
                      },
                    },
                  },
                }}
              >
                <MenuItem value="">Select use case</MenuItem>
                <MenuItem value="Internal IT Support">Internal IT Support</MenuItem>
                <MenuItem value="Customer Support">Customer Support</MenuItem>
                <MenuItem value="HR Support">HR Support</MenuItem>
                <MenuItem value="Operations Support">Operations Support</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </TextField>
              <TextField
                name="message"
                label="Additional Information"
                multiline
                rows={4}
                value={formData.message}
                onChange={handleChange}
                fullWidth
                variant="outlined"
                placeholder="Tell us about your support challenges or what you'd like to see in the demo..."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: mode === 'dark' ? '#1e293b !important' : 'rgba(248, 250, 252, 0.8)',
                    '& .MuiInputBase-input': {
                      color: mode === 'dark' ? '#ffffff !important' : '#0f172a !important',
                      WebkitTextFillColor: mode === 'dark' ? '#ffffff !important' : '#0f172a !important',
                    },
                    '& input': {
                      color: mode === 'dark' ? '#ffffff !important' : '#0f172a !important',
                      WebkitTextFillColor: mode === 'dark' ? '#ffffff !important' : '#0f172a !important',
                    },
                    '& input::placeholder': {
                      color: mode === 'dark' ? 'rgba(255, 255, 255, 0.5) !important' : '#94a3b8 !important',
                      opacity: 1,
                      WebkitTextFillColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.5) !important' : '#94a3b8 !important',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: mode === 'dark' ? 'rgba(71, 85, 105, 0.4)' : 'rgba(226, 232, 240, 0.8)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: mode === 'dark' ? 'rgba(99, 102, 241, 0.6)' : 'rgba(99, 102, 241, 0.4)',
                    },
                    '&.Mui-focused': {
                      backgroundColor: mode === 'dark' ? '#1e293b !important' : 'rgba(248, 250, 252, 0.8)',
                      '& .MuiInputBase-input': {
                        color: mode === 'dark' ? '#ffffff !important' : '#0f172a !important',
                        WebkitTextFillColor: mode === 'dark' ? '#ffffff !important' : '#0f172a !important',
                      },
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#6366f1',
                    },
                    '& textarea': {
                      color: mode === 'dark' ? '#ffffff !important' : '#0f172a !important',
                      WebkitTextFillColor: mode === 'dark' ? '#ffffff !important' : '#0f172a !important',
                      backgroundColor: 'transparent !important',
                      '&::placeholder': {
                        color: mode === 'dark' ? 'rgba(255, 255, 255, 0.5) !important' : '#94a3b8 !important',
                        opacity: 1,
                        WebkitTextFillColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.5) !important' : '#94a3b8 !important',
                      },
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : '#64748b',
                    '&.Mui-focused': {
                      color: '#6366f1',
                    },
                  },
                }}
              />
              {error && (
                <Alert 
                  severity="error" 
                  sx={{ 
                    mt: 1,
                    backgroundColor: mode === 'dark' ? 'rgba(239, 68, 68, 0.15)' : 'rgba(239, 68, 68, 0.1)',
                    color: mode === 'dark' ? '#f87171' : '#dc2626',
                    border: mode === 'dark' ? '1px solid rgba(239, 68, 68, 0.3)' : '1px solid rgba(239, 68, 68, 0.2)',
                    '& .MuiAlert-icon': {
                      color: mode === 'dark' ? '#f87171' : '#ef4444',
                    },
                  }}
                >
                  {error}
                </Alert>
              )}
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
                sx={{
                  mt: 2,
                  py: 1.5,
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #ec4899 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 50%, #db2777 100%)',
                  },
                }}
              >
                {loading ? 'Submitting...' : 'Request Demo'}
              </Button>
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DemoRequestForm;

